interface RateLimitEntry {
  count: number;
  windowStart: number;
  blockedUntil?: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.cleanupInterval = setInterval(() => this.cleanup(), 60000);
  }

  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.limits.entries()) {
      if (entry.blockedUntil && entry.blockedUntil < now) {
        this.limits.delete(key);
      } else if (!entry.blockedUntil && now - entry.windowStart > 3600000) {
        this.limits.delete(key);
      }
    }
  }

  private getKey(identifier: string, action: string): string {
    return `${action}:${identifier}`;
  }

  check(
    identifier: string,
    action: string,
    maxAttempts: number,
    windowMs: number
  ): { allowed: boolean; remainingAttempts: number; resetTime: number; blockedUntil?: number } {
    const key = this.getKey(identifier, action);
    const now = Date.now();
    const entry = this.limits.get(key);

    if (entry?.blockedUntil && entry.blockedUntil > now) {
      return {
        allowed: false,
        remainingAttempts: 0,
        resetTime: entry.blockedUntil,
        blockedUntil: entry.blockedUntil,
      };
    }

    if (!entry || now - entry.windowStart > windowMs) {
      this.limits.set(key, {
        count: 1,
        windowStart: now,
      });
      return {
        allowed: true,
        remainingAttempts: maxAttempts - 1,
        resetTime: now + windowMs,
      };
    }

    if (entry.count < maxAttempts) {
      entry.count++;
      return {
        allowed: true,
        remainingAttempts: maxAttempts - entry.count,
        resetTime: entry.windowStart + windowMs,
      };
    }

    return {
      allowed: false,
      remainingAttempts: 0,
      resetTime: entry.windowStart + windowMs,
    };
  }

  recordFailure(identifier: string, action: string, blockDurationMs?: number): void {
    const key = this.getKey(identifier, action);
    const entry = this.limits.get(key);

    if (entry && blockDurationMs) {
      entry.blockedUntil = Date.now() + blockDurationMs;
    }
  }

  reset(identifier: string, action: string): void {
    const key = this.getKey(identifier, action);
    this.limits.delete(key);
  }

  isBlocked(identifier: string, action: string): boolean {
    const key = this.getKey(identifier, action);
    const entry = this.limits.get(key);

    if (!entry?.blockedUntil) return false;

    if (entry.blockedUntil > Date.now()) {
      return true;
    }

    this.limits.delete(key);
    return false;
  }

  getBlockedUntil(identifier: string, action: string): number | null {
    const key = this.getKey(identifier, action);
    const entry = this.limits.get(key);

    if (!entry?.blockedUntil) return null;

    if (entry.blockedUntil > Date.now()) {
      return entry.blockedUntil;
    }

    return null;
  }

  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.limits.clear();
  }
}

export const rateLimiter = new RateLimiter();

export const RATE_LIMIT_CONFIGS = {
  LOGIN: { maxAttempts: 5, windowMs: 15 * 60 * 1000 },
  SIGNUP: { maxAttempts: 3, windowMs: 60 * 60 * 1000 },
  ORG_CREATION: { maxAttempts: 1, windowMs: 24 * 60 * 60 * 1000 },
  PASSWORD_RESET: { maxAttempts: 3, windowMs: 60 * 60 * 1000 },
  DATA_EXPORT: { maxAttempts: 5, windowMs: 60 * 60 * 1000 },
} as const;

export const calculateExponentialBackoff = (attemptNumber: number): number => {
  return Math.min(Math.pow(2, attemptNumber) * 1000, 30000);
};
